import {
  Activity,
  AlertTriangle,
  BookOpen,
  Check,
  Cpu,
  Database,
  ExternalLink,
  Eye,
  EyeOff,
  Gamepad,
  HardDrive,
  Network,
  PieChart,
  Power,
  Radio,
  RotateCw,
  Server,
  Settings,
  Sparkles,
  Target,
  Terminal,
  TrendingUp,
  Users
} from 'lucide-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useMonitorStream } from '../../hooks/useMonitorStream';
import { OnboardingContext } from '../../pages/GrandeEcole';
import useGrandeEcole from '../../stores/useGrandeEcole';
import CertificationDashboard from './CertificationDashboard';

const PLATFORM_LINKS = [
  {
    label: "Tableau Systémique des Elements",
    sub: 'Grammaire décisionnelle',
    route: '/tableau-systemique',
    icon: PieChart,
    style: {
      border: 'border-blue-900/30',
      bg: 'bg-blue-950/10',
      bgHover: 'hover:bg-blue-950/25',
      borderHover: 'hover:border-blue-800/50',
      iconBg: 'bg-blue-950/30',
      iconBorder: 'border-blue-800/30',
      iconColor: 'text-blue-400',
      label: 'text-blue-300',
      ext: 'group-hover:text-blue-500',
    },
  },  
  {
    label: "Atlas des Postures",
    sub: 'Cartographie & Scénarios',
    route: '/atlas-posture',
    icon: Users,
    style: {
      border: 'border-amber-900/30',
      bg: 'bg-amber-950/10',
      bgHover: 'hover:bg-amber-950/25',
      borderHover: 'hover:border-amber-800/50',
      iconBg: 'bg-amber-950/30',
      iconBorder: 'border-amber-800/30',
      iconColor: 'text-amber-400',
      label: 'text-amber-300',
      ext: 'group-hover:text-amber-500',
    },
  },
  {
    label: "Centre d'Ajustement",
    sub: 'Simulations & runs',
    route: '/centre-ajustement',
    icon: Target,
    style: {
      border: 'border-indigo-900/30',
      bg: 'bg-indigo-950/10',
      bgHover: 'hover:bg-indigo-950/25',
      borderHover: 'hover:border-indigo-800/50',
      iconBg: 'bg-indigo-950/30',
      iconBorder: 'border-indigo-800/30',
      iconColor: 'text-indigo-400',
      label: 'text-indigo-300',
      ext: 'group-hover:text-indigo-500',
    },
  },
  {
    label: 'Parcours',
    sub: 'Bibliothèque des parcours',
    route: '/parcours',
    icon: BookOpen,
    style: {
      border: 'border-orange-900/30',
      bg: 'bg-orange-950/10',
      bgHover: 'hover:bg-orange-950/25',
      borderHover: 'hover:border-orange-800/50',
      iconBg: 'bg-orange-950/30',
      iconBorder: 'border-orange-800/30',
      iconColor: 'text-orange-400',
      label: 'text-orange-300',
      ext: 'group-hover:text-orange-500',
    },
  },  
  {
    label: 'Ateliers',
    sub: 'Sessions live & théorie',
    route: '/ateliers',
    icon: Sparkles,
    style: {
      border: 'border-pink-900/30',
      bg: 'bg-pink-950/10',
      bgHover: 'hover:bg-pink-950/25',
      borderHover: 'hover:border-pink-800/50',
      iconBg: 'bg-pink-950/30',
      iconBorder: 'border-pink-800/30',
      iconColor: 'text-pink-400',
      label: 'text-pink-300',
      ext: 'group-hover:text-pink-500',
    },
  },
  {
    label: 'Expériences',
    sub: 'Apprentissage & Société',
    route: '/pesa-exp',
    icon: Gamepad,
    style: {
      border: 'border-green-900/30',
      bg: 'bg-green-950/10',
      bgHover: 'hover:bg-green-950/25',
      borderHover: 'hover:border-green-800/50',
      iconBg: 'bg-green-950/30',
      iconBorder: 'border-green-800/30',
      iconColor: 'text-green-400',
      label: 'text-green-300',
      ext: 'group-hover:text-green-500',
    },
  },
  
];

const QUICK_ACTIONS = [
  {
    label: 'Flush Cache',
    icon: RotateCw,
    key: 'flushLearnerCache',
    style: {
      border: 'border-sky-900/40',
      bg: 'bg-sky-950/20',
      bgHover: 'hover:bg-sky-950/40',
      icon: 'text-sky-400',
    },
  },
  {
    label: 'Restart Services',
    icon: Power,
    key: 'restartServices',
    style: {
      border: 'border-amber-900/40',
      bg: 'bg-amber-950/20',
      bgHover: 'hover:bg-amber-950/40',
      icon: 'text-amber-400',
    },
  },
  {
    label: 'Run Backup',
    icon: Database,
    key: 'runBackup',
    style: {
      border: 'border-emerald-900/40',
      bg: 'bg-emerald-950/20',
      bgHover: 'hover:bg-emerald-950/40',
      icon: 'text-emerald-400',
    },
  },
  {
    label: 'Health Check',
    icon: Activity,
    key: 'runSystemHealthCheck',
    style: {
      border: 'border-purple-900/40',
      bg: 'bg-purple-950/20',
      bgHover: 'hover:bg-purple-950/40',
      icon: 'text-purple-400',
    },
  },
  {
    label: 'View Metrics',
    icon: TrendingUp,
    key: 'scrollTop',
    style: {
      border: 'border-orange-900/40',
      bg: 'bg-orange-950/20',
      bgHover: 'hover:bg-orange-950/40',
      icon: 'text-orange-400',
    },
  },
  {
    label: 'System Config',
    icon: Settings,
    key: 'config',
    style: {
      border: 'border-zinc-800/60',
      bg: 'bg-zinc-900/40',
      bgHover: 'hover:bg-zinc-800/60',
      icon: 'text-zinc-400',
    },
  },
];

const getStatusBgColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'online':  return 'bg-emerald-400';
    case 'warning': return 'bg-amber-400';
    case 'error':   return 'bg-red-400';
    case 'offline': return 'bg-zinc-500';
    default:        return 'bg-zinc-500';
  }
};

const getMetricColor = (value) => {
  if (value > 80) return 'text-red-400';
  if (value > 60) return 'text-amber-400';
  return 'text-emerald-400';
};

const getBarColor = (value) => {
  if (value > 80) return 'bg-red-500';
  if (value > 60) return 'bg-amber-500';
  return 'bg-emerald-500';
};

const getLogStyle = (level) => {
  switch (level?.toUpperCase()) {
    case 'ERROR': return { row: 'text-red-400 bg-red-950/30 border-red-800/40',   text: 'text-red-400'   };
    case 'WARN':  return { row: 'text-amber-400 bg-amber-950/30 border-amber-800/40', text: 'text-amber-400' };
    case 'INFO':  return { row: 'text-sky-400 bg-sky-950/30 border-sky-800/40',   text: 'text-sky-400'   };
    case 'DEBUG': return { row: 'text-zinc-400 bg-zinc-950/30 border-zinc-800/40', text: 'text-zinc-400' };
    default:      return { row: 'text-zinc-400 bg-zinc-950/30 border-zinc-800/40', text: 'text-zinc-400' };
  }
};


function MetricCard({ icon: Icon, iconColor, label, value, barValue }) {
  return (
    <div className="border border-zinc-800 bg-black/40 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className={iconColor} />
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">{label}</span>
        </div>
        <span className={`text-2xl font-black font-mono ${getMetricColor(barValue)}`}>
          {value}
        </span>
      </div>
      <div className="relative h-2 bg-zinc-900 overflow-hidden">
        <div
          className={`absolute h-full transition-all duration-1000 ${getBarColor(barValue)}`}
          style={{ width: `${Math.min(100, barValue || 0)}%` }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

export function DashboardEngine({ darkMode = true }) {
  const {
    metrics: liveMetrics = { cpu: 0, memory: 0, network: 0, storage: 0 },
    services: liveServices = [],
    systemLogs: liveLogs = [],
    connected,
  } = useMonitorStream();

  const { openOnboarding } = useContext(OnboardingContext);

  const {
    logsEnabled,
    loading,
    totalLearners = 0,
    activeLearners = 0,
    totalPesasCirculating = 0,
    globalCompletionRate = 0,
    criticalAlerts = 0,
    systemAlerts = [],
    toggleLogs,
    clearLogs,
    restartServices,
    restartSingleService,
    learners,
    courses,
    cohortes,
    pesaCases,
    qualiData,
    qualiLoading,
    qualiError,
    dashboardMeta,
    fetchQualiopiData,
    fetchDashboardData,
    onboardingCompleted
  } = useGrandeEcole();

  const { user } = useAuth();
  const tenantId = user?.tenantId || user?.tenant?._id;

  const [showAllLearners, setShowAllLearners] = useState(false);

  const generateReleve      = useGrandeEcole((s) => s.generateReleve);
  const flushLearnerCache   = useGrandeEcole((s) => s.flushLearnerCache);
  const runBackup           = useGrandeEcole((s) => s.runBackup);
  const runSystemHealthCheck = useGrandeEcole((s) => s.runSystemHealthCheck);
  

  // FIX #6 — compteur de critiques calculé depuis systemAlerts pour cohérence affichage/badge
  const trueCriticalCount = systemAlerts.filter(
    (a) => a.severity === 'critical' || a.severity === 'emergency'
  ).length;

  const handleQuickAction = useCallback(
    async (key) => {
      switch (key) {
        case 'flushLearnerCache':
          await flushLearnerCache();
          break;
        case 'restartServices':
          await restartServices();
          break;
        case 'runBackup':
          await runBackup();
          break;
        case 'runSystemHealthCheck':
          try {
            const result = await runSystemHealthCheck();
            alert(`Health OK – Score: ${result?.score ?? 'N/A'}/100`);
          } catch (err) {
            alert('Health check échoué : ' + err.message);
          }
          break;
        case 'scrollTop':
          window.scrollTo({ top: 0, behavior: 'smooth' });
          break;
        case 'config':
          alert('Configuration système – en développement');
          break;
        default:
          break;
      }
    },
    [flushLearnerCache, restartServices, runBackup, runSystemHealthCheck]
  );

  useEffect(() => {
    //fetchQualiopiData();
    fetchDashboardData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps



  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-zinc-800 animate-pulse rounded" />
          ))}
        </div>
        <div className="h-96 bg-zinc-800 animate-pulse rounded" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      
      {trueCriticalCount > 0 && (
        <AlertsSection
          trueCriticalCount={trueCriticalCount}
          systemAlerts={systemAlerts}
        />
      )}
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={Cpu}
          iconColor="text-orange-400"
          label="CPU Load"
          value={`${liveMetrics.cpu?.toFixed(1) ?? '?'}%`}
          barValue={liveMetrics.cpu}
        />
        <MetricCard
          icon={Database}
          iconColor="text-sky-400"
          label="Memory"
          value={`${liveMetrics.memory?.toFixed(1) ?? '?'}%`}
          barValue={liveMetrics.memory}
        />
        <MetricCard
          icon={Network}
          iconColor="text-purple-400"
          label="Network I/O"
          value={`${liveMetrics.network?.toFixed(1) ?? '?'}%`}
          barValue={liveMetrics.network}
        />
        <MetricCard
          icon={HardDrive}
          iconColor="text-emerald-400"
          label="Storage"
          value={`${liveMetrics.storage?.toFixed(1) ?? '?'}%`}
          barValue={liveMetrics.storage}
        />
      </div>
      
      <CertificationDashboard
        qualiData={qualiData}
        qualiLoading={qualiLoading}
        qualiError={qualiError}
        generator={dashboardMeta?.generator}
        learners={learners}
        generateReleve={generateReleve}
        tenantId={tenantId}
      />

      {!onboardingCompleted && (
      <button onClick={openOnboarding} className="px-4 py-2 text-xs bg-sky-600 hover:bg-sky-700 text-white rounded">
        Finaliser la configuration
      </button>
    )}
      
      <div className="border border-zinc-800 bg-black/40 p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <ExternalLink size={14} className="text-orange-400" />
          <span className="text-sm font-black uppercase tracking-wider text-orange-300">
            Accès Plateforme
          </span>
          <span className="text-[10px] text-zinc-600 ml-auto font-mono uppercase tracking-widest">
            Vue_Apprenant
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PLATFORM_LINKS.map(({ label, sub, route, icon: Icon, style }) => (
            <button
              key={route}
              onClick={() => window.open(route, '_blank')}
              className={`group flex items-center gap-4 p-4 border ${style.border} ${style.bg} ${style.bgHover} ${style.borderHover} transition-all text-left`}
            >
              <div className={`p-2.5 border ${style.iconBorder} ${style.iconBg} flex-shrink-0`}>
                <Icon size={16} className={style.iconColor} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-black ${style.label} uppercase tracking-tight`}>
                  {label}
                </div>
                <div className="text-[10px] text-zinc-600 font-mono truncate mt-0.5">{sub}</div>
              </div>
              <ExternalLink
                size={12}
                className={`text-zinc-700 ${style.ext} transition-colors flex-shrink-0`}
              />
            </button>
          ))}
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Services système */}
        <div className="border border-zinc-800 bg-black/40">
          <div className="border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Server size={14} className={connected ? 'text-emerald-400' : 'text-red-400'} />
              <span className="text-sm font-black uppercase tracking-wider text-orange-300">
                Services Système
              </span>
              {!connected && (
                <span className="text-[10px] text-red-400 font-mono">(Déconnecté)</span>
              )}
            </div>
          </div>

          <div className="divide-y divide-zinc-800/40 max-h-96 overflow-y-auto">
            {liveServices.map((service) => (
              <div
                key={service.id}
                className="px-5 py-3 flex items-center justify-between hover:bg-zinc-900/30"
              >
                <div className="flex items-center gap-3 flex-1">
                  {/* FIX #1 — classe CSS complète via getStatusBgColor */}
                  <div
                    className={`w-2 h-2 rounded-full ${getStatusBgColor(service.status)} animate-pulse flex-shrink-0`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm font-medium text-zinc-200 truncate">
                      {service.name}
                    </div>
                    <div className="text-[10px] text-zinc-600 font-mono">
                      uptime : {service.uptime}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right">
                      <div className={`text-sm font-black font-mono ${getMetricColor(service.load)}`}>
                        {service.load ?? 0}%
                      </div>
                    </div>
                    <div className="w-20 h-1.5 bg-zinc-900 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${getBarColor(service.load)}`}
                        style={{ width: `${service.load ?? 0}%` }}
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => restartSingleService(service.id)}
                  className="p-1.5 ml-4 text-zinc-600 hover:text-emerald-400 transition-colors flex-shrink-0"
                  title="Redémarrer ce service"
                >
                  <RotateCw size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Activité plateforme live */}
        <div className="border border-zinc-800 bg-black/40 flex flex-col">
          <div className="border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Activity size={14} className="text-emerald-400" />
              <span className="text-sm font-black uppercase tracking-wider text-emerald-300">
                Activité Plateforme
              </span>
              <div className="flex items-center gap-1 ml-2">
                <Radio size={10} className="text-emerald-500 animate-pulse" />
                <span className="text-[9px] text-emerald-500 uppercase">Live</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-zinc-600 font-mono">{activeLearners} actifs</span>
              <button
                onClick={toggleLogs}
                className="p-1 hover:bg-zinc-800 rounded transition-colors"
                title={logsEnabled ? 'Masquer les logs' : 'Afficher les logs'}
              >
                {logsEnabled
                  ? <Eye size={14} className="text-emerald-400" />
                  : <EyeOff size={14} className="text-zinc-600" />}
              </button>
              <button
                onClick={clearLogs}
                className="p-1 hover:bg-zinc-800 rounded transition-colors"
                title="Effacer le flux"
              >
                <RotateCw size={14} className="text-zinc-600" />
              </button>
            </div>
          </div>

          {/* Snapshot métier */}
          <div className="grid grid-cols-5 divide-x divide-zinc-800 border-b border-zinc-800">
            {[
              { label: 'Actifs',     value: activeLearners,          color: 'text-emerald-400' },
              { label: 'Total',      value: totalLearners,            color: 'text-zinc-400'   },
              { label: 'Parcours',   value: courses.length,           color: 'text-orange-400' },
              { label: 'Complétion', value: `${globalCompletionRate}%`, color: 'text-sky-400'  },
              {
                label: 'Alertes',
                value: criticalAlerts,
                color: criticalAlerts > 0 ? 'text-red-400' : 'text-zinc-600',
              },
            ].map(({ label, value, color }) => (
              <div key={label} className="px-3 py-2.5 text-center">
                <div className={`text-lg font-black font-mono ${color}`}>{value}</div>
                <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">
                  {label}
                </div>
              </div>
            ))}
          </div>

          {/* Flux d'événements */}
          <div className="flex-1 overflow-y-auto h-64 font-mono text-[10px] p-3 space-y-1 bg-black/60">
          {learners
              .filter((l) => l.status === 'active')
              .slice(0, 5)
              .map((l, idx) => (
                <div
                  key={`learner-${l._id || idx}`}
                  className="px-2 py-1.5 border-l-2 border-emerald-800/50 bg-emerald-950/10 flex items-center gap-3"
                >
                  <Users size={10} className="text-emerald-500 flex-shrink-0" />
                  <span className="text-emerald-400 font-bold w-16 flex-shrink-0 truncate">ACTIF</span>
                  <span className="text-white-600 flex-1 truncate">
                    {l.firstName || l.lastName
                      ? `${l.firstName ?? ''} ${l.lastName ?? ''}`.trim()
                      : l.email || l.userId}
                  </span>
                  <span className="text-zinc-600 flex-shrink-0">
                    {l.globalProgress != null ? `${l.globalProgress}%` : '—'}
                  </span>
                </div>
              ))}

            {/* Parcours actifs */}
            {courses.slice(0, 3).map((c, idx) => (
              <div
                key={`course-${c._id || idx}`}
                className="px-2 py-1.5 border-l-2 border-orange-800/50 bg-orange-950/10 flex items-center gap-3"
              >
                <BookOpen size={10} className="text-orange-500 flex-shrink-0" />
                <span className="text-orange-400 font-bold w-16 flex-shrink-0 truncate">PARCOURS</span>
                <span className="text-white-600 flex-1 truncate">{c.nom || c.title || c.slug}</span>
                <span className="text-zinc-600 flex-shrink-0 uppercase text-[9px]">
                  {c.status || c.visibility || '—'}
                </span>
              </div>
            ))}

            {/* Simulations PESA */}
            {pesaCases.slice(0, 2).map((pesa, idx) => (
              <div
                key={`pesa-${pesa._id || idx}`}
                className="px-2 py-1.5 border-l-2 border-rose-800/50 bg-rose-950/10 flex items-center gap-3"
              >
                <TrendingUp size={10} className="text-rose-500 flex-shrink-0" />
                <span className="text-rose-400 font-bold w-16 flex-shrink-0 truncate">Etudes de Cas</span>
                <span className="text-white-600 flex-1 truncate">
                  {pesa.title || `Cas #${idx + 1}`}
                </span>
                <span className="text-zinc-600 flex-shrink-0 uppercase text-[9px]">
                  Difficulté: {pesa.difficulty || '—'}
                </span>
              </div>
            ))}

            {/* Cohortes */}
            {cohortes.slice(0, 2).map((co, idx) => (
              <div
                key={`cohorte-${co._id || idx}`}
                className="px-2 py-1.5 border-l-2 border-sky-800/50 bg-sky-950/10 flex items-center gap-3"
              >
                <Users size={10} className="text-sky-500 flex-shrink-0" />
                <span className="text-sky-400 font-bold w-16 flex-shrink-0 truncate">COHORTE</span>
                <span className="text-white-600 flex-1 truncate">{co.nom || co.name}</span>
                <span className="text-zinc-600 flex-shrink-0">
                  {co.stats?.nbEleves ?? co.eleves ?? 0} app.
                </span>
              </div>
            ))}

            {/* Alertes critiques dans le flux */}
            {systemAlerts.slice(0, 3).map((a, idx) => (
              <div
                key={`alert-${a.id || idx}`}
                className="px-2 py-1.5 border-l-2 border-red-800/50 bg-red-950/10 flex items-center gap-3"
              >
                <AlertTriangle size={10} className="text-red-500 flex-shrink-0" />
                <span className="text-red-400 font-bold w-16 flex-shrink-0 truncate">
                  {a.severity?.toUpperCase() || 'ALERTE'}
                </span>
                <span className="text-white-600 flex-1 truncate">
                  {a.message || a.details || 'Alerte système'}
                </span>
                <span className="text-zinc-600 flex-shrink-0 text-[9px]">
                  {a.timestamp ? new Date(a.timestamp).toLocaleTimeString('fr-FR') : '—'}
                </span>
              </div>
            ))}

            {/* Logs système si activés — FIX #3 — objet logStyle, pas de double appel */}
            {logsEnabled &&
              liveLogs.slice(0, 5).map((log, idx) => {
                const logStyle = getLogStyle(log.level);
                return (
                  <div
                    key={`log-${log.id || idx}`}
                    className={`px-2 py-1 border-l-2 ${logStyle.row} flex items-center gap-3`}
                  >
                    <Terminal size={10} className="flex-shrink-0 opacity-50" />
                    <span className={`font-bold w-16 flex-shrink-0 ${logStyle.text}`}>
                      {log.level || 'INFO'}
                    </span>
                    <span className="text-white-600 flex-1 truncate">
                      {log.msg || log.message || '—'}
                    </span>
                    <span className="text-zinc-600 flex-shrink-0 text-[9px]">
                      {log.timestamp
                        ? new Date(log.timestamp).toLocaleTimeString('fr-FR')
                        : '—'}
                    </span>
                  </div>
                );
              })}

            {/* État vide */}
            {learners.length === 0 &&
              courses.length === 0 &&
              cohortes.length === 0 &&
              liveLogs.length === 0 && (
                <div className="h-full flex items-center justify-center text-zinc-600">
                  <div className="text-center">
                    <Activity size={24} className="mx-auto mb-2 opacity-40" />
                    <div>Aucune activité détectée</div>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {QUICK_ACTIONS.map((action) => (
          <button
            key={action.key}
            onClick={() => handleQuickAction(action.key)}
            className={`group p-4 border ${action.style.border} ${action.style.bg} ${action.style.bgHover} transition-all flex flex-col items-center gap-2 text-center`}
          >
            <action.icon
              size={18}
              className={`${action.style.icon} group-hover:scale-110 transition-transform`}
            />
            <span className="text-[10px] text-zinc-400 group-hover:text-white-600 uppercase tracking-wide">
              {action.label}
            </span>
          </button>
        ))}
      </div>
      
      {trueCriticalCount === 0 && (
        <AlertsSection
          trueCriticalCount={trueCriticalCount}
          systemAlerts={systemAlerts}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION ALERTES — extraite pour être réutilisée en haut ET en bas
// ─────────────────────────────────────────────────────────────────────────────

function AlertsSection({ trueCriticalCount, systemAlerts }) {
  return (
    <div className="border border-red-900/50 bg-gradient-to-b from-red-950/20 to-black/40 p-5">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-red-900/40">
        <div className="flex items-center gap-2.5">
          <AlertTriangle
            size={16}
            className={`text-red-400 ${trueCriticalCount > 0 ? 'animate-pulse' : ''}`}
          />
          <span className="font-medium text-red-300 uppercase tracking-wide text-sm">
            Incidents Actifs
          </span>
          {/* FIX #6 — badge cohérent avec le filtre critique/emergency */}
          <span className="px-2 py-0.5 bg-red-900/40 text-red-300 text-[10px] font-black rounded-sm">
            {trueCriticalCount} CRITIQUES
          </span>
          <span className="text-[10px] text-zinc-500 ml-2">
            {systemAlerts.length} alertes totales
          </span>
        </div>
        <button className="text-xs px-3 py-1 border border-red-800 hover:bg-red-950/40 rounded text-red-300/90 transition-colors">
          Voir tous
        </button>
      </div>

      {systemAlerts.length === 0 ? (
        <div className="text-center py-8 text-zinc-500">
          <Check size={32} className="mx-auto mb-2 text-emerald-500 opacity-60" />
          <p>Aucune alerte critique en cours</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
          {systemAlerts
            .filter((a) => a.severity === 'critical' || a.severity === 'emergency')
            .slice(0, 5)
            .map((alert, idx) => (
              <div
                key={alert.id || idx}
                className="p-3 border-l-4 border-red-600 bg-red-950/30 flex items-start justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-red-300">
                    {alert.type?.toUpperCase() || 'ALERTE CRITIQUE'}
                  </div>
                  <p className="text-white-600 text-sm mt-1">
                    {alert.message || alert.details || 'Détails non disponibles'}
                  </p>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    {alert.timestamp
                      ? new Date(alert.timestamp).toLocaleString('fr-FR')
                      : 'Récemment'}
                    {alert.target && ` • ${alert.target}`}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-2 shrink-0">
                  <button className="text-[10px] px-2 py-1 bg-red-900/40 text-red-300 hover:bg-red-900/60 rounded transition-colors">
                    TRAITER
                  </button>
                  <button className="text-[10px] px-2 py-1 bg-zinc-800/60 text-white-600 hover:bg-zinc-700 rounded transition-colors">
                    IGNORER
                  </button>
                </div>
              </div>
            ))}

          {trueCriticalCount > 5 && (
            <div className="text-center text-[11px] text-zinc-500 py-2">
              + {trueCriticalCount - 5} autres alertes critiques
            </div>
          )}
        </div>
      )}
    </div>
  );
}